/**
 * MicroPanel — daily micronutrient readout (T13.3 / ISSUE-076).
 *
 * Free: fibre, sodium, potassium, calcium (basic credibility set).
 * Premium: all 10 micros + additive gap-fill tips (never "deficient" framing).
 *
 * Gap framing is strictly additive (brand-guide §2, D-08):
 *   "Adding X could help you reach your fibre reference" — never "you lack" or
 *   "you're short". No sodium/sugar/saturated-fat gap suggestions (not additive).
 */
import type { components } from '@nutians/api-client';
import { MICRO_DAILY_REFERENCES } from '@nutians/shared/schemas';
import { useTranslation } from 'react-i18next';
import { PremiumGate } from './PremiumGate.js';

type DaySummary = components['schemas']['DaySummary'];
type MicroSet = NonNullable<DaySummary['micros']>;

// ── Config ────────────────────────────────────────────────────────────────────

/** Free-tier micro keys — enough to be credible. */
const FREE_KEYS: (keyof MicroSet)[] = ['fiberG', 'sodiumMg', 'potassiumMg', 'calciumMg'];

/** All 10 micro keys in display order. */
const ALL_KEYS: (keyof MicroSet)[] = [
  'fiberG',
  'sugarG',
  'saturatedFatG',
  'sodiumMg',
  'potassiumMg',
  'calciumMg',
  'ironMg',
  'magnesiumMg',
  'vitaminDUg',
  'vitaminB12Ug',
];

const UNITS: Record<keyof MicroSet, string> = {
  fiberG: 'g',
  sugarG: 'g',
  saturatedFatG: 'g',
  sodiumMg: 'mg',
  potassiumMg: 'mg',
  calciumMg: 'mg',
  ironMg: 'mg',
  magnesiumMg: 'mg',
  vitaminDUg: 'µg',
  vitaminB12Ug: 'µg',
};

/** Neutral food examples for additive gap suggestions (no diet-food editorializing). */
const GAP_FOODS: Partial<Record<keyof MicroSet, string>> = {
  fiberG: 'lentils, oats, or berries',
  potassiumMg: 'banana, sweet potato, or white beans',
  calciumMg: 'yogurt or leafy greens',
  ironMg: 'lentils, tofu, or spinach',
  magnesiumMg: 'pumpkin seeds or almonds',
  vitaminDUg: 'oily fish, eggs, or mushrooms',
  vitaminB12Ug: 'eggs, fish, or fortified cereals',
};

/** Gap threshold: suggest when below 70% of reference. */
const GAP_THRESHOLD = 0.7;

// ── Helpers ───────────────────────────────────────────────────────────────────

function roundDisplay(v: number, unit: string): string {
  if (unit === 'µg') return v.toFixed(1);
  return v < 10 ? v.toFixed(1) : String(Math.round(v));
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface MicroRowProps {
  label: string;
  value: number | null;
  unit: string;
  reference: number | null;
  gapFoods?: string;
  showGap: boolean;
}

function MicroRow({ label, value, unit, reference, gapFoods, showGap }: MicroRowProps) {
  const { t } = useTranslation('progress');
  const pct =
    value !== null && reference !== null && reference > 0
      ? Math.min(100, (value / reference) * 100)
      : null;

  const refLabel =
    reference !== null ? t('micros.ref', { value: Math.round(reference), unit }) : null;

  const showGapTip =
    showGap &&
    gapFoods !== undefined &&
    value !== null &&
    reference !== null &&
    value / reference < GAP_THRESHOLD;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-ink dark:text-ink">{label}</span>
        <span className="text-ink-soft dark:text-ink-soft">
          {value !== null ? (
            <>
              <span>{roundDisplay(value, unit)}</span>
              <span className="ml-0.5">{unit}</span>
              {refLabel && (
                <span className="ml-1 text-ink-muted dark:text-ink-muted">/ {refLabel}</span>
              )}
            </>
          ) : (
            <>
              <span aria-hidden="true">—</span>
              <span className="sr-only">no data</span>
            </>
          )}
        </span>
      </div>

      {/* Progress bar toward reference */}
      {pct !== null && (
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-husk dark:bg-shell"
          role="progressbar"
          aria-valuenow={Math.round(value ?? 0)}
          aria-valuemin={0}
          aria-valuemax={reference ?? 0}
          aria-label={`${label}: ${Math.round(pct)}% of daily reference`}
        >
          <div
            className="h-full rounded-full bg-mist/80 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Additive gap tip — premium only, additive micros only */}
      {showGapTip && (
        <p className="text-xs text-ink-soft dark:text-ink-soft">
          {t('micros.gapTip', { foods: gapFoods, micro: label.toLowerCase() })}
        </p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface MicroPanelProps {
  micros: MicroSet | null;
  isPremium: boolean;
  className?: string;
}

export function MicroPanel({ micros, isPremium, className = '' }: MicroPanelProps) {
  const { t } = useTranslation('progress');
  const keys = isPremium ? ALL_KEYS : FREE_KEYS;

  return (
    <section
      className={`rounded-lg border border-husk bg-shell px-4 py-3 dark:border-ink/20 dark:bg-leaf ${className}`}
      aria-label="Daily micronutrients"
    >
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
        {t('micros.title')}
      </h3>

      {micros === null ? (
        <p className="text-xs text-ink-soft dark:text-ink-soft">{t('micros.noData')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {keys.map((key) => (
            <MicroRow
              key={key}
              label={t(`micros.fields.${key}`)}
              value={micros[key]}
              unit={UNITS[key]}
              reference={MICRO_DAILY_REFERENCES[key]}
              gapFoods={GAP_FOODS[key]}
              showGap={isPremium}
            />
          ))}
        </div>
      )}

      {/* Teaser for free users — show below the basic readout */}
      {!isPremium && micros !== null && (
        <div className="mt-4">
          <PremiumGate feature="nutrient_panel" heading={t('micros.premiumTeaser')} />
        </div>
      )}
    </section>
  );
}
